import React from "react";
import { motion } from "framer-motion";
import { Home, Grid3X3, Briefcase, ArrowUpRight } from "lucide-react";
import { services } from "../content";

const iconMap = { Home, Grid3X3, Briefcase };

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-bone py-24 md:py-32 border-t border-charcoal/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-12 gap-y-8 md:gap-x-10 mb-14 md:mb-20"
        >
          <div className="col-span-12 md:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-10 h-px bg-taupe" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-taupe">
                What we do
              </span>
            </div>
            <h2
              data-testid="services-headline"
              className="font-serif text-charcoal text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em]"
            >
              Three disciplines.<br className="hidden md:block" /> One studio.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex md:items-end">
            <p className="text-charcoal/70 leading-relaxed">
              From a first-time apartment to a flagship workspace, every project
              begins with the same intent: design that quietly outlasts trends.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-charcoal/10">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon] || Home;
            return (
              <motion.article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-100px" }}
                className="group relative bg-bone p-8 md:p-10 lg:p-12 flex flex-col min-h-[340px] hover:bg-[#F2EFE9] transition-colors duration-500"
              >
                <div className="flex items-center justify-between">
                  <span
                    className="inline-flex items-center justify-center w-12 h-12 border border-taupe/40 text-taupe transition-colors duration-500 group-hover:border-charcoal group-hover:text-charcoal"
                    aria-hidden
                  >
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <span className="font-serif italic text-taupe text-sm">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="mt-10 font-serif text-charcoal text-2xl md:text-3xl tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-charcoal/70 leading-relaxed text-[15px]">
                  {s.description}
                </p>

                <ul className="mt-6 space-y-2">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-[13px] text-charcoal/80 flex items-center gap-3"
                    >
                      <span className="block w-4 h-px bg-taupe" />
                      {b}
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  data-testid={`service-cta-${s.id}`}
                  className="mt-auto pt-8 inline-flex items-center gap-2 text-sm text-charcoal border-b border-charcoal/30 self-start pb-1 hover:border-charcoal transition-colors duration-300"
                >
                  Start a Brief
                  <ArrowUpRight size={14} />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
